import {
  Content,
  ContentHeader,
  Header,
  InfoCard,
  Link,
  Page,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import DescriptionIcon from '@material-ui/icons/Description';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ReceiptIcon from '@material-ui/icons/Receipt';
import SecurityIcon from '@material-ui/icons/Security';
import TimelineIcon from '@material-ui/icons/Timeline';
import WarningIcon from '@material-ui/icons/Warning';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

type CatalogEntity = {
  kind?: string;
  metadata?: {
    name?: string;
    namespace?: string;
    title?: string;
    description?: string;
    tags?: string[];
  };
  spec?: {
    owner?: string;
    type?: string;
    steps?: Array<{
      id?: string;
      name?: string;
      action?: string;
    }>;
    parameters?: Array<{
      title?: string;
      required?: string[];
      properties?: Record<string, unknown>;
    }>;
  };
};

type ScaffolderAction = {
  id?: string;
  description?: string;
  schema?: {
    input?: unknown;
    output?: unknown;
  };
};

type RuntimeState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | {
      status: 'ready';
      templates: CatalogEntity[];
      actions: ScaffolderAction[];
      products: CatalogEntity[];
    };

type FactoryActionField = {
  name: string;
  label: string;
  helperText: string;
  required?: boolean;
  pattern?: string;
};

type FactoryActionDefinition = {
  key: string;
  title: string;
  description: string;
  templateName: string;
  fields: FactoryActionField[];
};

type NativeActionState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success'; taskId: string }
  | { status: 'error'; message: string };

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius * 3,
    background:
      'linear-gradient(135deg, rgba(11,31,50,0.96), rgba(23,63,89,0.9))',
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(4),
  },
  heroTitle: {
    maxWidth: 760,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  metricGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    marginTop: theme.spacing(3),
  },
  metricCard: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
  },
  workflowCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  workflowMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  workflowFooter: {
    marginTop: 'auto',
    paddingTop: theme.spacing(3),
  },
  actionConsole: {
    marginBottom: theme.spacing(4),
  },
  consoleShell: {
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius * 3,
    border: `1px solid ${theme.palette.divider}`,
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.94), rgba(245,248,250,0.88))',
  },
  consoleGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    marginTop: theme.spacing(2),
  },
  consoleStep: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  consoleActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  actionList: {
    display: 'grid',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  nativeFormShell: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
  },
  nativeFormGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    marginTop: theme.spacing(2),
  },
  nativeFormActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  actionItem: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  principleCard: {
    height: '100%',
  },
  statusCard: {
    marginBottom: theme.spacing(3),
  },
  loading: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
}));

const operatingPrinciples = [
  {
    icon: <SecurityIcon color="primary" />,
    title: 'Writes Stay Governed',
    description:
      'The portal launches workflows, but the authoritative write path remains the repo-owned or harness-owned command contract.',
  },
  {
    icon: <TimelineIcon color="primary" />,
    title: 'Evidence Stays With The Product',
    description:
      'Opportunity, feature, and package artifacts remain in the target repo so audit evidence and delivery traceability stay local to product truth.',
  },
  {
    icon: <ReceiptIcon color="primary" />,
    title: 'Portal UX, Real Services',
    description:
      'This surface resolves live catalog entities and live scaffolder actions before exposing an operator launch path.',
  },
];

const quickLinks = [
  {
    icon: <AccountTreeIcon color="primary" />,
    title: 'Browse The Catalog',
    description: 'Review registered product repos, owners, and systems.',
    href: '/',
  },
  {
    icon: <DescriptionIcon color="primary" />,
    title: 'Open Portal Docs',
    description: 'See the runtime spec and published TechDocs for this portal.',
    href: '/docs/default/component/c4ci-product-factory-portal',
  },
  {
    icon: <PlayArrowIcon color="primary" />,
    title: 'Review Workflow Actions',
    description:
      'Inspect the registered scaffolder actions that back portal workflows.',
    href: '/create/actions',
  },
];

const portalActionDefinitions: FactoryActionDefinition[] = [
  {
    key: 'create-opportunity',
    title: 'Create Opportunity',
    description:
      'Create a repo-owned delivery opportunity record through the target repo command contract.',
    templateName: 'c4ci-create-delivery-opportunity',
    fields: [
      {
        name: 'targetRepo',
        label: 'Target repo',
        helperText: 'Catalog entity ref or repo id.',
        required: true,
      },
      {
        name: 'title',
        label: 'Opportunity title',
        helperText: 'Short title for the repo-owned opportunity artifact.',
        required: true,
      },
      {
        name: 'slug',
        label: 'Slug override',
        helperText: 'Optional lowercase slug.',
        pattern: '^[a-z0-9-]+$',
      },
      {
        name: 'owner',
        label: 'Owner override',
        helperText: 'Optional owner override; blank keeps the repo default.',
      },
    ],
  },
  {
    key: 'promote-opportunity',
    title: 'Promote Opportunity',
    description:
      'Promote an approved opportunity into feature records through the target repo command contract.',
    templateName: 'c4ci-promote-opportunity-to-feature',
    fields: [
      {
        name: 'targetRepo',
        label: 'Target repo',
        helperText: 'Catalog entity ref or repo id.',
        required: true,
      },
      {
        name: 'opportunityId',
        label: 'Opportunity ID',
        helperText: 'Use the OPP-012 format.',
        required: true,
        pattern: '^OPP-[0-9]{3}$',
      },
    ],
  },
  {
    key: 'scaffold-feature',
    title: 'Scaffold Feature Package',
    description:
      'Refresh companion feature package artifacts through the target repo command contract.',
    templateName: 'c4ci-scaffold-feature-package',
    fields: [
      {
        name: 'targetRepo',
        label: 'Target repo',
        helperText: 'Catalog entity ref or repo id.',
        required: true,
      },
      {
        name: 'featureId',
        label: 'Feature ID',
        helperText: 'Use the FEAT-004 format.',
        required: true,
        pattern: '^FEAT-[0-9]{3}$',
      },
    ],
  },
  {
    key: 'run-validators',
    title: 'Run Validators',
    description:
      'Run the repo-owned delivery-readiness validator without replacing the repo as source of truth.',
    templateName: 'c4ci-run-product-validators',
    fields: [
      {
        name: 'targetRepo',
        label: 'Target repo',
        helperText: 'Catalog entity ref or repo id.',
        required: true,
      },
    ],
  },
];

function entityName(entity: CatalogEntity): string {
  return entity.metadata?.name ?? 'unknown';
}

function entityNamespace(entity: CatalogEntity): string {
  return entity.metadata?.namespace ?? 'default';
}

function entityTitle(entity: CatalogEntity): string {
  return entity.metadata?.title ?? entityName(entity);
}

function entityDescription(entity: CatalogEntity): string {
  return (
    entity.metadata?.description ??
    'Registered C4CI workflow template with live catalog metadata.'
  );
}

function templateActionIds(entity: CatalogEntity): string[] {
  return (entity.spec?.steps ?? [])
    .map(step => step.action)
    .filter((action): action is string => Boolean(action));
}

function requiredParameterCount(entity: CatalogEntity): number {
  return (entity.spec?.parameters ?? []).reduce(
    (total, group) => total + (group.required?.length ?? 0),
    0,
  );
}

function c4ciTags(entity: CatalogEntity): string[] {
  return [
    ...(entity.metadata?.tags ?? []),
    entity.spec?.owner ? `owner:${entity.spec.owner}` : undefined,
    entity.spec?.type ? `type:${entity.spec.type}` : undefined,
  ].filter((tag): tag is string => Boolean(tag));
}

function productIdentity(entity: CatalogEntity): string {
  return `${entityNamespace(entity)}/${entityName(entity)}`;
}

function productEntityRef(entity: CatalogEntity): string {
  return `component:${productIdentity(entity)}`;
}

function entityRoute(entity: CatalogEntity): string {
  return `/catalog/${entityNamespace(entity)}/${
    entity.kind?.toLowerCase() ?? 'component'
  }/${entityName(entity)}`;
}

function templateLaunchRoute(template: CatalogEntity): string {
  return `/create/templates/${entityNamespace(template)}/${entityName(
    template,
  )}`;
}

function templateEntityRef(templateName: string): string {
  return `template:default/${templateName}`;
}

function productToken(entity: CatalogEntity): string {
  return entityName(entity)
    .replace(/^c4ci-product-/, '')
    .replace(/^c4ci-/, '');
}

function templateMatchesProduct(
  template: CatalogEntity,
  product: CatalogEntity,
): boolean {
  const token = productToken(product);
  const searchable = [
    entityName(template),
    template.metadata?.title,
    template.metadata?.description,
    ...(template.metadata?.tags ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return searchable.includes(token.toLowerCase());
}

async function fetchJson<T>(fetchApi: FetchApi, url: string): Promise<T> {
  const response = await fetchApi.fetch(url);
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`.trim());
  }
  return (await response.json()) as T;
}

function catalogEntitiesUrl(baseUrl: string, kind: string): string {
  const url = new URL(`${baseUrl}/entities`);
  url.searchParams.set('filter', `kind=${kind}`);
  url.searchParams.set('fields', 'kind,metadata,spec');
  return url.toString();
}

function parseActions(payload: unknown): ScaffolderAction[] {
  if (Array.isArray(payload)) {
    return payload as ScaffolderAction[];
  }

  if (
    payload &&
    typeof payload === 'object' &&
    'actions' in payload &&
    Array.isArray((payload as { actions?: unknown }).actions)
  ) {
    return (payload as { actions: ScaffolderAction[] }).actions;
  }

  return [];
}

function isC4ciTemplate(entity: CatalogEntity): boolean {
  const name = entityName(entity);
  return (
    name.startsWith('c4ci-') || (entity.metadata?.tags ?? []).includes('c4ci')
  );
}

function isC4ciProduct(entity: CatalogEntity): boolean {
  const name = entityName(entity);
  return (
    name.startsWith('c4ci-') || (entity.metadata?.tags ?? []).includes('c4ci')
  );
}

function isC4ciAction(action: ScaffolderAction): boolean {
  return action.id?.startsWith('c4ci:delivery:') ?? false;
}

function useFactoryRuntimeState(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): RuntimeState {
  const [state, setState] = useState<RuntimeState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function loadRuntimeState() {
      setState({ status: 'loading' });
      try {
        const [catalogBaseUrl, scaffolderBaseUrl] = await Promise.all([
          discoveryApi.getBaseUrl('catalog'),
          discoveryApi.getBaseUrl('scaffolder'),
        ]);

        const [templates, products, actionsPayload] = await Promise.all([
          fetchJson<CatalogEntity[]>(
            fetchApi,
            catalogEntitiesUrl(catalogBaseUrl, 'Template'),
          ),
          fetchJson<CatalogEntity[]>(
            fetchApi,
            catalogEntitiesUrl(catalogBaseUrl, 'Component'),
          ),
          fetchJson<unknown>(fetchApi, `${scaffolderBaseUrl}/v2/actions`),
        ]);

        if (!cancelled) {
          setState({
            status: 'ready',
            templates: templates.filter(isC4ciTemplate),
            products: products.filter(isC4ciProduct),
            actions: parseActions(actionsPayload).filter(isC4ciAction),
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            status: 'error',
            message:
              error instanceof Error
                ? error.message
                : 'Unable to load live factory runtime state',
          });
        }
      }
    }

    loadRuntimeState();

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  return state;
}

function NativeFactoryActionForms({
  selectedProduct,
}: {
  selectedProduct: CatalogEntity;
}) {
  const classes = useStyles();
  const navigate = useNavigate();
  const scaffolderApi = useApi(scaffolderApiRef);
  const defaultTargetRepo = productEntityRef(selectedProduct);
  const [selectedActionKey, setSelectedActionKey] = useState(
    portalActionDefinitions[0].key,
  );
  const [values, setValues] = useState<Record<string, string>>({
    targetRepo: defaultTargetRepo,
  });
  const [actionState, setActionState] = useState<NativeActionState>({
    status: 'idle',
  });
  const actionErrorRef = useRef<HTMLElement | null>(null);

  const selectedAction =
    portalActionDefinitions.find(action => action.key === selectedActionKey) ??
    portalActionDefinitions[0];

  useEffect(() => {
    setValues(current => ({
      ...current,
      targetRepo: defaultTargetRepo,
    }));
  }, [defaultTargetRepo]);

  useEffect(() => {
    if (actionState.status === 'error') {
      actionErrorRef.current?.focus();
    }
  }, [actionState]);

  async function launchSelectedAction() {
    setActionState({ status: 'submitting' });

    const invalidField = selectedAction.fields.find(field => {
      const value = (values[field.name] ?? '').trim();
      if (field.required && !value) {
        return true;
      }
      if (field.pattern && value && !new RegExp(field.pattern).test(value)) {
        return true;
      }
      return false;
    });

    if (invalidField) {
      setActionState({
        status: 'error',
        message: `${invalidField.label} is required or does not match the expected format.`,
      });
      return;
    }

    const payload = Object.fromEntries(
      selectedAction.fields.flatMap(field => {
        const value = (values[field.name] ?? '').trim();
        return value ? [[field.name, value]] : [];
      }),
    );

    try {
      const { taskId } = await scaffolderApi.scaffold({
        templateRef: templateEntityRef(selectedAction.templateName),
        values: payload,
      });
      setActionState({ status: 'success', taskId });
      navigate(`/create/tasks/${taskId}`);
    } catch (error) {
      setActionState({
        status: 'error',
        message:
          error instanceof Error
            ? error.message
            : 'Unable to launch the selected workflow',
      });
    }
  }

  return (
    <Paper className={classes.nativeFormShell} elevation={0}>
      <Typography variant="h6">Portal-Native Action Launcher</Typography>
      <Typography variant="body2" color="textSecondary">
        These forms submit directly to the live Backstage Scaffolder task API.
        Each task still resolves the target repo and executes the repo-owned
        command declared in `project.yaml`.
      </Typography>
      <div className={classes.nativeFormGrid}>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="factory-native-action-label">
            Action family
          </InputLabel>
          <Select
            label="Action family"
            labelId="factory-native-action-label"
            value={selectedActionKey}
            onChange={event => {
              setSelectedActionKey(event.target.value as string);
              setActionState({ status: 'idle' });
            }}
          >
            {portalActionDefinitions.map(action => (
              <MenuItem key={action.key} value={action.key}>
                {action.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedAction.fields.map(field => (
          <TextField
            fullWidth
            helperText={field.helperText}
            id={`factory-native-${selectedAction.key}-${field.name}`}
            inputProps={field.pattern ? { pattern: field.pattern } : undefined}
            key={field.name}
            label={field.label}
            name={field.name}
            onChange={event =>
              setValues(current => ({
                ...current,
                [field.name]: event.target.value,
              }))
            }
            required={field.required}
            value={values[field.name] ?? ''}
            variant="outlined"
          />
        ))}
      </div>
      <Box mt={2}>
        <Typography variant="body2" color="textSecondary">
          {selectedAction.description}
        </Typography>
      </Box>
      {actionState.status === 'error' ? (
        <Box mt={2}>
          <Typography
            color="error"
            ref={actionErrorRef}
            role="alert"
            tabIndex={-1}
            variant="body2"
          >
            {actionState.message}
          </Typography>
        </Box>
      ) : null}
      {actionState.status === 'success' ? (
        <Box mt={2}>
          <Typography variant="body2">
            Started task{' '}
            <Link
              component={RouterLink}
              to={`/create/tasks/${actionState.taskId}`}
            >
              {actionState.taskId}
            </Link>
            .
          </Typography>
        </Box>
      ) : null}
      <div className={classes.nativeFormActions}>
        <Button
          color="primary"
          disabled={actionState.status === 'submitting'}
          onClick={launchSelectedAction}
          startIcon={<PlayArrowIcon />}
          variant="contained"
        >
          {actionState.status === 'submitting'
            ? 'Starting Task'
            : 'Start Governed Task'}
        </Button>
        <Button component={RouterLink} to="/create/tasks" variant="outlined">
          View Workflow Tasks
        </Button>
      </div>
    </Paper>
  );
}

export const FactoryWorkflowsPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const runtimeState = useFactoryRuntimeState(discoveryApi, fetchApi);
  const [selectedProductId, setSelectedProductId] = useState('');
  const actionsById = useMemo(() => {
    if (runtimeState.status !== 'ready') {
      return new Map<string, ScaffolderAction>();
    }

    return new Map(
      runtimeState.actions
        .filter(action => Boolean(action.id))
        .map(action => [action.id as string, action]),
    );
  }, [runtimeState]);

  const templateCount =
    runtimeState.status === 'ready' ? runtimeState.templates.length : 0;
  const actionCount =
    runtimeState.status === 'ready' ? runtimeState.actions.length : 0;
  const productCount =
    runtimeState.status === 'ready' ? runtimeState.products.length : 0;
  const selectedProduct =
    runtimeState.status === 'ready'
      ? runtimeState.products.find(
          product => productIdentity(product) === selectedProductId,
        ) ?? runtimeState.products[0]
      : undefined;
  const selectedTemplates =
    runtimeState.status === 'ready' && selectedProduct
      ? runtimeState.templates.filter(template =>
          templateMatchesProduct(template, selectedProduct),
        )
      : [];
  let visibleConsoleTemplates: CatalogEntity[] = [];
  if (selectedTemplates.length > 0) {
    visibleConsoleTemplates = selectedTemplates;
  } else if (runtimeState.status === 'ready') {
    visibleConsoleTemplates = runtimeState.templates;
  }
  const selectedActionIds = Array.from(
    new Set(visibleConsoleTemplates.flatMap(templateActionIds)),
  );
  const registeredSelectedActions = selectedActionIds.flatMap(actionId => {
    const action = actionsById.get(actionId);
    return action ? [action] : [];
  });

  useEffect(() => {
    if (runtimeState.status !== 'ready' || runtimeState.products.length === 0) {
      return;
    }

    if (
      !selectedProductId ||
      !runtimeState.products.some(
        product => productIdentity(product) === selectedProductId,
      )
    ) {
      setSelectedProductId(productIdentity(runtimeState.products[0]));
    }
  }, [runtimeState, selectedProductId]);

  return (
    <Page themeId="home">
      <Header
        title="Factory Workflows"
        subtitle="Portal-native delivery entry points for the C4CI product factory"
      />
      <Content>
        <Box className={classes.hero}>
          <div className={classes.chipRow}>
            <Chip color="primary" label="Portal-native UX" />
            <Chip label="Live catalog" variant="outlined" />
            <Chip label="Live scaffolder actions" variant="outlined" />
            <Chip label="Repo-command governance" variant="outlined" />
          </div>
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            className={classes.heroTitle}
          >
            Launch governed product-factory workflows from the portal surface
            operators actually use.
          </Typography>
          <Typography variant="body1">
            This page resolves the Backstage catalog and scaffolder API at
            runtime, then exposes only registered C4CI templates and delivery
            actions. If the backend is unavailable, the portal says so instead
            of showing stale workflow cards.
          </Typography>
          <div className={classes.metricGrid}>
            <div className={classes.metricCard}>
              <Typography variant="caption">C4CI templates</Typography>
              <Typography variant="h4">{templateCount}</Typography>
            </div>
            <div className={classes.metricCard}>
              <Typography variant="caption">Delivery actions</Typography>
              <Typography variant="h4">{actionCount}</Typography>
            </div>
            <div className={classes.metricCard}>
              <Typography variant="caption">Product components</Typography>
              <Typography variant="h4">{productCount}</Typography>
            </div>
          </div>
        </Box>

        {runtimeState.status === 'loading' ? (
          <InfoCard
            className={classes.statusCard}
            title="Loading live runtime state"
          >
            <Box className={classes.loading}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="textSecondary">
                Resolving Backstage discovery, catalog entities, and scaffolder
                action registry.
              </Typography>
            </Box>
          </InfoCard>
        ) : null}

        {runtimeState.status === 'error' ? (
          <InfoCard
            className={classes.statusCard}
            title="Live workflow registry unavailable"
          >
            <Box display="flex" alignItems="center" mb={2}>
              <WarningIcon color="error" />
              <Box ml={1}>
                <Typography variant="body2" color="textSecondary">
                  The portal could not fetch the real Backstage runtime data:{' '}
                  {runtimeState.message}
                </Typography>
              </Box>
            </Box>
            <Button
              color="primary"
              component={RouterLink}
              startIcon={<TimelineIcon />}
              to="/create/actions"
              variant="outlined"
            >
              Open Action Registry
            </Button>
          </InfoCard>
        ) : null}

        <ContentHeader title="Governed Delivery Workflows">
          <Button
            color="primary"
            component={RouterLink}
            startIcon={<TimelineIcon />}
            to="/create/tasks"
            variant="outlined"
          >
            View Workflow Tasks
          </Button>
        </ContentHeader>

        {runtimeState.status === 'ready' && selectedProduct ? (
          <Box className={classes.actionConsole}>
            <InfoCard title="Portal Action Console">
              <Box className={classes.consoleShell}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="factory-product-select-label">
                        Live catalog product
                      </InputLabel>
                      <Select
                        label="Live catalog product"
                        labelId="factory-product-select-label"
                        value={productIdentity(selectedProduct)}
                        onChange={event =>
                          setSelectedProductId(event.target.value as string)
                        }
                      >
                        {runtimeState.products.map(product => (
                          <MenuItem
                            key={productIdentity(product)}
                            value={productIdentity(product)}
                          >
                            {entityTitle(product)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Typography variant="h6">
                      {entityTitle(selectedProduct)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {entityDescription(selectedProduct)}
                    </Typography>
                    <Box className={classes.consoleActions}>
                      <Button
                        color="primary"
                        component={RouterLink}
                        to={entityRoute(selectedProduct)}
                        variant="contained"
                      >
                        Open Catalog Entity
                      </Button>
                      <Button
                        color="primary"
                        component={RouterLink}
                        to={`${entityRoute(selectedProduct)}/docs`}
                        variant="outlined"
                      >
                        Open Product Docs
                      </Button>
                      <Button
                        component={RouterLink}
                        to="/create/tasks"
                        variant="outlined"
                      >
                        Review Workflow Tasks
                      </Button>
                    </Box>
                  </Grid>
                </Grid>

                <div className={classes.consoleGrid}>
                  <div className={classes.consoleStep}>
                    <Typography variant="subtitle2">1. Inspect</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Start from the live catalog entity, ownership metadata,
                      TechDocs, and product annotations before creating work.
                    </Typography>
                  </div>
                  <div className={classes.consoleStep}>
                    <Typography variant="subtitle2">
                      2. Select Workflow
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedTemplates.length > 0
                        ? `${selectedTemplates.length} template(s) match this product identity.`
                        : 'No product-specific template match was found; showing all registered C4CI templates.'}
                    </Typography>
                  </div>
                  <div className={classes.consoleStep}>
                    <Typography variant="subtitle2">
                      3. Launch Governed Run
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Use Backstage Scaffolder forms for parameter entry, task
                      tracking, and the registered C4CI delivery action path.
                    </Typography>
                  </div>
                  <div className={classes.consoleStep}>
                    <Typography variant="subtitle2">
                      4. Verify Evidence
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Return to this console after the task to check catalog,
                      docs, workflow tasks, and delivery action registry state.
                    </Typography>
                  </div>
                </div>

                <NativeFactoryActionForms selectedProduct={selectedProduct} />

                <Box mt={3}>
                  <Divider />
                  <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                    Launchable workflows for this product
                  </Typography>
                  <div className={classes.actionList}>
                    {visibleConsoleTemplates.map(template => (
                      <div
                        className={classes.actionItem}
                        key={entityName(template)}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={8}>
                            <Typography variant="body2">
                              <strong>{entityTitle(template)}</strong>
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {entityDescription(template)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Button
                              color="primary"
                              component={RouterLink}
                              fullWidth
                              startIcon={<PlayArrowIcon />}
                              to={templateLaunchRoute(template)}
                              variant="contained"
                            >
                              Launch
                            </Button>
                          </Grid>
                        </Grid>
                      </div>
                    ))}
                  </div>
                </Box>

                <Box mt={3}>
                  <Divider />
                  <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                    Registered action coverage for this selection
                  </Typography>
                  <div className={classes.actionList}>
                    {registeredSelectedActions.length > 0 ? (
                      registeredSelectedActions.map(action => (
                        <div className={classes.actionItem} key={action.id}>
                          <Typography variant="body2">
                            <strong>{action.id}</strong>
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {action.description ??
                              'Registered C4CI scaffolder action with runtime schema.'}
                          </Typography>
                        </div>
                      ))
                    ) : (
                      <div className={classes.actionItem}>
                        <Typography variant="body2" color="textSecondary">
                          No matching registered C4CI action is attached to the
                          selected template set. Open the action registry before
                          launching a workflow.
                        </Typography>
                      </div>
                    )}
                  </div>
                </Box>
              </Box>
            </InfoCard>
          </Box>
        ) : null}

        {runtimeState.status === 'ready' &&
        runtimeState.templates.length === 0 ? (
          <InfoCard
            className={classes.statusCard}
            title="No C4CI templates registered"
          >
            <Typography variant="body2" color="textSecondary">
              The catalog responded successfully, but it did not return any
              Template entity tagged or named for C4CI. Register the product
              factory catalog package before launching workflows.
            </Typography>
          </InfoCard>
        ) : null}

        {runtimeState.status === 'ready' ? (
          <Grid container spacing={3}>
            {runtimeState.templates.map(template => {
              const actionIds = templateActionIds(template);
              const firstAction = actionIds[0];
              const registeredAction = firstAction
                ? actionsById.get(firstAction)
                : undefined;
              const launchPath = templateLaunchRoute(template);

              return (
                <Grid item xs={12} md={4} key={entityName(template)}>
                  <InfoCard
                    className={classes.workflowCard}
                    title={entityTitle(template)}
                    subheader={
                      registeredAction?.description ??
                      `${requiredParameterCount(
                        template,
                      )} required launch field${
                        requiredParameterCount(template) === 1 ? '' : 's'
                      }`
                    }
                  >
                    <Typography variant="body2" color="textSecondary">
                      {entityDescription(template)}
                    </Typography>
                    <div className={classes.workflowMeta}>
                      {c4ciTags(template).map(tag => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                      {actionIds.map(actionId => (
                        <Chip
                          key={actionId}
                          color={
                            actionsById.has(actionId) ? 'primary' : 'default'
                          }
                          label={actionId}
                          size="small"
                        />
                      ))}
                    </div>
                    <div className={classes.workflowFooter}>
                      <Button
                        color="primary"
                        component={RouterLink}
                        startIcon={<PlayArrowIcon />}
                        to={launchPath}
                        variant="contained"
                      >
                        Launch Workflow
                      </Button>
                    </div>
                  </InfoCard>
                </Grid>
              );
            })}
          </Grid>
        ) : null}

        <Box mt={4}>
          <ContentHeader title="Operating Principles" />
          <Grid container spacing={3}>
            {operatingPrinciples.map(principle => (
              <Grid item xs={12} md={4} key={principle.title}>
                <InfoCard
                  className={classes.principleCard}
                  title={principle.title}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    {principle.icon}
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {principle.description}
                  </Typography>
                </InfoCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mt={4}>
          <ContentHeader title="Quick Links" />
          <Grid container spacing={3}>
            {quickLinks.map(link => (
              <Grid item xs={12} md={4} key={link.title}>
                <InfoCard title={link.title}>
                  <Box display="flex" alignItems="center" mb={2}>
                    {link.icon}
                  </Box>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {link.description}
                  </Typography>
                  <Link component={RouterLink} to={link.href}>
                    Open
                  </Link>
                </InfoCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Content>
    </Page>
  );
};
